import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  useAdminFetchUserSuggestionsQuery,
  useAdminCreateSuggestionMutation,
  useAdminGenerateConversationMutation,
  useAdminVectorMatchMutation,
  useAdminSendSuggestionCardMutation,
  useAdminRegenerateSuggestionReasonMutation,
  useAdminUpdateSuggestionReasonMutation,
} from 'src/store/suggestionsApi';
import { Feedback, SuggestionType } from 'src/store/suggestionsApi';
import { Badge } from 'src/ui/Badge';
import { Button } from 'src/ui/Button';
import { Card } from 'src/ui/Card';
import { Modal } from 'src/ui/Modal';
import { Spinner } from 'src/ui/Spinner';
import { Textarea } from 'src/ui/Textarea';
import { useToast } from 'src/ui/Toast';
import { UserPickerPanel } from 'src/ui/patterns/UserPickerPanel';
import { UserEntity } from 'src/store/userApi';
import { api } from 'src/store/api';

interface UserSuggestionsProps {
  userId: string | null;
  onChatClick?: (userConversationId: string) => void;
}

const pct = (n: number | null | undefined) =>
  n == null ? 'n/a' : `${Math.round(n * 100)}%`;

const UserSuggestions = ({ userId, onChatClick }: UserSuggestionsProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTargetUser, setSelectedTargetUser] =
    useState<UserEntity | null>(null);
  const push = useToast();
  const dispatch = useDispatch();

  const { data, isLoading, error, refetch } = useAdminFetchUserSuggestionsQuery(
    {
      userId: userId || '',
      limit: 100,
      offset: 0,
    },
    {
      skip: !userId,
    }
  );

  const [createSuggestion, { isLoading: isCreating }] =
    useAdminCreateSuggestionMutation();

  const [vectorMatch, { isLoading: isVectorMatching }] =
    useAdminVectorMatchMutation();

  const [generateConversation, { isLoading: isGenerating }] =
    useAdminGenerateConversationMutation();
  const [generatingSuggestionId, setGeneratingSuggestionId] = useState<
    string | null
  >(null);

  const [sendSuggestionCard] = useAdminSendSuggestionCardMutation();
  const [sendingCardId, setSendingCardId] = useState<string | null>(null);

  // Regenerate reason/hook: a per-card editable draft shown below the active
  // copy (keyed by suggestion id, since this is a list of cards). Saving writes
  // the draft onto the suggestion record and clears the draft.
  const [regenerateReason] = useAdminRegenerateSuggestionReasonMutation();
  const [regeneratingReasonId, setRegeneratingReasonId] = useState<
    string | null
  >(null);
  const [updateReason] = useAdminUpdateSuggestionReasonMutation();
  const [savingReasonId, setSavingReasonId] = useState<string | null>(null);
  const [reasonDrafts, setReasonDrafts] = useState<
    Record<string, { hook: string; reason: string }>
  >({});

  const updateReasonDraft = (
    suggestionId: string,
    field: 'hook' | 'reason',
    value: string
  ) =>
    setReasonDrafts((prev) => {
      const current = prev[suggestionId] ?? { hook: '', reason: '' };
      return { ...prev, [suggestionId]: { ...current, [field]: value } };
    });

  const handleDiscardReason = (suggestionId: string) =>
    setReasonDrafts((prev) => {
      const next = { ...prev };
      delete next[suggestionId];
      return next;
    });

  const handleRegenerateReason = async (suggestionId: string) => {
    setRegeneratingReasonId(suggestionId);
    try {
      const result = await regenerateReason({ suggestionId }).unwrap();
      if (result.success && result.data) {
        const { hook, reason } = result.data;
        setReasonDrafts((prev) => ({
          ...prev,
          [suggestionId]: { hook, reason },
        }));
      } else {
        push({
          title: 'Notice',
          message: result.message || 'Failed to regenerate reason',
          type: 'warning',
        });
      }
    } catch (error: any) {
      push({
        title: 'Error',
        message: error?.data?.message || 'Failed to regenerate reason',
        type: 'error',
      });
    } finally {
      setRegeneratingReasonId(null);
    }
  };

  const handleSaveReason = async (suggestionId: string) => {
    const draft = reasonDrafts[suggestionId];
    if (!draft) return;
    setSavingReasonId(suggestionId);
    try {
      await updateReason({
        suggestionId,
        hook: draft.hook,
        reason: draft.reason,
      }).unwrap();
      push({
        title: 'Success',
        message: 'Suggestion reason saved',
        type: 'success',
      });
      handleDiscardReason(suggestionId);
      refetch();
    } catch (error: any) {
      push({
        title: 'Error',
        message: error?.data?.message || 'Failed to save reason',
        type: 'error',
      });
    } finally {
      setSavingReasonId(null);
    }
  };

  const handleSendSuggestionCard = async (suggestionId: string) => {
    setSendingCardId(suggestionId);
    try {
      const result = await sendSuggestionCard({ suggestionId }).unwrap();
      push({
        title: result.success ? 'Success' : 'Notice',
        message: result.message || 'Suggestion card sent',
        type: result.success ? 'success' : 'warning',
      });
    } catch (error: any) {
      push({
        title: 'Error',
        message: error?.data?.message || 'Failed to send suggestion card',
        type: 'error',
      });
    } finally {
      setSendingCardId(null);
    }
  };

  const handleViewChat = (userConversationId?: string) => {
    if (!userConversationId) return;
    if (onChatClick) {
      onChatClick(userConversationId);
    }
  };

  const handleGenerateConversation = async (
    suggestionId: string,
    targetUserId?: string
  ) => {
    setGeneratingSuggestionId(suggestionId);
    try {
      const result = await generateConversation({ suggestionId }).unwrap();
      push({
        title: 'Success',
        message: result.message || 'Conversation generated',
        type: 'success',
      });
      refetch();
      if (userId) {
        dispatch(
          api.util.invalidateTags([
            { type: 'UserMatches', id: userId },
            ...(targetUserId
              ? [{ type: 'UserMatches' as const, id: targetUserId }]
              : []),
          ])
        );
      }
    } catch (error: any) {
      push({
        title: 'Error',
        message: error?.data?.message || 'Failed to generate conversation',
        type: 'error',
      });
    } finally {
      setGeneratingSuggestionId(null);
    }
  };

  const handleCreateSuggestion = async () => {
    if (!userId || !selectedTargetUser) {
      push({
        title: 'Error',
        message: 'Please select a user to create suggestion',
        type: 'error',
      });
      return;
    }

    if (userId === selectedTargetUser.id) {
      push({
        title: 'Error',
        message: 'Cannot create suggestion with the same user',
        type: 'error',
      });
      return;
    }

    try {
      const result = await createSuggestion({
        userId,
        targetUserId: selectedTargetUser.id,
      }).unwrap();

      // The server message carries delivery status and, for admin-forced
      // pairs, a gate warning — surface it verbatim.
      push({
        title: result.data?.gated ? 'Created with warning' : 'Success',
        message: result.message || 'Suggestion created successfully',
        type:
          result.data?.gated || result.data?.cardSent === false
            ? 'warning'
            : 'success',
      });

      setSelectedTargetUser(null);
      setIsOpen(false);
      refetch();

      dispatch(
        api.util.invalidateTags([
          { type: 'UserMatches', id: userId },
          { type: 'UserMatches', id: selectedTargetUser.id },
        ])
      );
    } catch (error: any) {
      push({
        title: 'Error',
        message: error?.data?.message || 'Failed to create suggestion',
        type: 'error',
      });
    }
  };

  const handleVectorMatch = async () => {
    if (!userId) return;

    try {
      const result = await vectorMatch({ userId }).unwrap();

      // The endpoint returns HTTP 200 with { success: false } when there are no
      // eligible candidates left, so check the body — unwrap() only throws on
      // transport/HTTP errors.
      if (!result.success) {
        push({
          title: 'No match generated',
          message: result.message || 'No eligible candidates available',
          type: 'warning',
        });
        return;
      }

      push({
        title: 'Success',
        // Server message notes whether the card reached the user's chat.
        message: result.message || 'Vector match suggestion created',
        type: 'success',
      });

      refetch();
      dispatch(api.util.invalidateTags([{ type: 'UserMatches', id: userId }]));
    } catch (error: any) {
      push({
        title: 'Error',
        message: error?.data?.message || 'Failed to generate vector match',
        type: 'error',
      });
    }
  };

  if (!userId) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <span style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-lg)' }}>
          Select a user to view their suggestions
        </span>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <span
          style={{
            color: 'var(--red-solid, #dc2626)',
            fontSize: 'var(--fs-lg)',
          }}
        >
          Failed to load suggestions
        </span>
      </div>
    );
  }

  const suggestions = data?.data || [];

  const getFeedbackBadge = (feedback?: Feedback, isViewed?: boolean) => {
    if (feedback === Feedback.LIKE) {
      return <Badge color="green">Liked</Badge>;
    }
    if (feedback === Feedback.REJECT) {
      return <Badge color="red">Passed</Badge>;
    }
    if (isViewed) {
      return <Badge color="amber">Viewed</Badge>;
    }
    return <Badge color="gray">Not Viewed</Badge>;
  };

  const getSuggestionTypeLabel = (type: SuggestionType) => {
    if (type === SuggestionType.LIKE_YOU) {
      return 'Likes You';
    }
    return 'Match';
  };

  const createSuggestionModal = (
    <Modal
      open={isOpen}
      onClose={() => setIsOpen(false)}
      title="Create suggestion from user"
      width={680}
      footer={
        <div className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateSuggestion}
            disabled={isCreating || !selectedTargetUser}
          >
            {isCreating ? 'Creating…' : 'Create suggestion from user'}
          </Button>
        </div>
      }
    >
      <div className="col" style={{ gap: 16 }}>
        <span
          style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-sm)' }}
        >
          Select a user to create a suggestion with{' '}
          {userId ? 'the selected user' : 'this user'}.
        </span>
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            height: 400,
            overflow: 'hidden',
          }}
        >
          <UserPickerPanel
            selectedUserId={selectedTargetUser?.id}
            onSelect={(user) => setSelectedTargetUser(user)}
            excludeUserIds={userId ? [userId] : undefined}
            title="Select a user"
            height={400}
          />
        </div>
        {selectedTargetUser && (
          <div
            style={{
              padding: 12,
              background: 'var(--bg-canvas)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-subtle)',
            }}
          >
            <div
              style={{
                fontWeight: 600,
                fontSize: 'var(--fs-sm)',
                color: 'var(--text)',
              }}
            >
              Selected User: {selectedTargetUser.profile?.name || 'Unknown'}
            </div>
            <div
              style={{
                fontSize: 'var(--fs-sm)',
                color: 'var(--text-secondary)',
              }}
            >
              {selectedTargetUser.email}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );

  if (suggestions.length === 0) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <span style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-lg)' }}>
          No suggestions found for this user
        </span>
        <div
          className="row"
          style={{ gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}
        >
          <Button variant="primary" onClick={() => setIsOpen(true)}>
            Create suggestion from user
          </Button>
          <Button
            variant="secondary"
            onClick={handleVectorMatch}
            disabled={isVectorMatching}
          >
            {isVectorMatching ? 'Generating…' : 'Generate Vector Match'}
          </Button>
        </div>
        {createSuggestionModal}
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div
        style={{
          padding: 24,
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}
      >
        <div
          className="row"
          style={{ justifyContent: 'space-between', alignItems: 'center' }}
        >
          <span
            style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-sm)' }}
          >
            Total Suggestions: {suggestions.length}
          </span>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <Button size="sm" variant="primary" onClick={() => setIsOpen(true)}>
              Create suggestion from user
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={handleVectorMatch}
              disabled={isVectorMatching}
            >
              {isVectorMatching ? 'Generating…' : 'Generate Vector Match'}
            </Button>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
        <div className="col" style={{ gap: 16 }}>
          {suggestions.map((suggestion) => {
            const matchedUser = suggestion.match?.userB;
            const firstPhoto = matchedUser?.photos?.[0];
            const photoUrl =
              firstPhoto?.sImage?.url ||
              firstPhoto?.mImage?.url ||
              firstPhoto?.lImage?.url;
            const name =
              matchedUser?.profile?.name || matchedUser?.email || 'Unknown';
            const reasonDraft = reasonDrafts[suggestion.id];

            return (
              <Card key={suggestion.id} pad>
                <div
                  className="row"
                  style={{ gap: 16, alignItems: 'flex-start' }}
                >
                  {/* Photo */}
                  {photoUrl && (
                    <div style={{ flexShrink: 0 }}>
                      <img
                        src={photoUrl}
                        alt={name}
                        style={{
                          width: 80,
                          height: 80,
                          objectFit: 'cover',
                          borderRadius: 'var(--radius-md)',
                        }}
                      />
                    </div>
                  )}

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      className="row"
                      style={{
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: 8,
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: 'var(--fs-md)',
                            color: 'var(--text)',
                          }}
                        >
                          {name}
                        </div>
                        <div
                          style={{
                            fontSize: 'var(--fs-sm)',
                            color: 'var(--text-secondary)',
                          }}
                        >
                          {matchedUser?.email}
                        </div>
                      </div>
                      <div
                        className="row"
                        style={{ gap: 8, alignItems: 'center' }}
                      >
                        {getFeedbackBadge(
                          suggestion.feedback,
                          suggestion.isViewed
                        )}
                        {suggestion.mutualLike && (
                          <Badge color="purple">Mutual Like</Badge>
                        )}
                        <Badge color="gray">
                          {getSuggestionTypeLabel(suggestion.type)}
                        </Badge>
                      </div>
                    </div>

                    {/* Match Info */}
                    {/* Vector match score + explainable breakdown */}
                    {suggestion.match?.score != null && (
                      <div style={{ marginBottom: 8 }}>
                        <div
                          className="row"
                          style={{
                            gap: 8,
                            alignItems: 'center',
                            flexWrap: 'wrap',
                          }}
                        >
                          <Badge
                            color={
                              suggestion.match.score >= 0.5 ? 'green' : 'gray'
                            }
                            size="sm"
                          >
                            Match score: {suggestion.match.score.toFixed(2)}
                          </Badge>
                          {suggestion.match.breakdown?.gated && (
                            <Badge color="red" size="sm">
                              gated:{' '}
                              {suggestion.match.breakdown.gateReasons.join(
                                ', '
                              )}
                            </Badge>
                          )}
                        </div>
                        {suggestion.match.breakdown && (
                          <div
                            className="row"
                            style={{
                              gap: 12,
                              flexWrap: 'wrap',
                              fontSize: 'var(--fs-xs)',
                              color: 'var(--text-muted)',
                              marginTop: 4,
                            }}
                          >
                            <span>
                              pref A→B{' '}
                              {pct(suggestion.match.breakdown.prefSatAtoB)} ·
                              B→A {pct(suggestion.match.breakdown.prefSatBtoA)}
                            </span>
                            <span>
                              homophily{' '}
                              {pct(suggestion.match.breakdown.homophily)}
                            </span>
                            <span>
                              vibe {pct(suggestion.match.breakdown.vibe)}
                            </span>
                            <span>
                              geo {pct(suggestion.match.breakdown.geo)}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Active hook + reason */}
                    {(suggestion.hook || suggestion.reason) && (
                      <div style={{ marginBottom: 8 }}>
                        {suggestion.hook && (
                          <div
                            style={{
                              fontSize: 'var(--fs-sm)',
                              fontWeight: 600,
                              color: 'var(--text)',
                              marginBottom: 4,
                            }}
                          >
                            {suggestion.hook}
                          </div>
                        )}
                        {suggestion.reason && (
                          <div
                            style={{
                              fontSize: 'var(--fs-sm)',
                              color: 'var(--text)',
                            }}
                          >
                            {suggestion.reason}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Regenerated draft (below the active copy) — edit then Save */}
                    {reasonDraft && (
                      <div style={{ marginTop: 8, marginBottom: 8 }}>
                        <div
                          style={{
                            fontSize: 'var(--fs-xs)',
                            fontWeight: 600,
                            color: 'var(--text-secondary)',
                            marginBottom: 6,
                          }}
                        >
                          Generated reason &amp; hook
                        </div>
                        <Card pad>
                          <div className="col" style={{ gap: 8 }}>
                            <label
                              style={{
                                fontSize: 'var(--fs-xs)',
                                color: 'var(--text-muted)',
                              }}
                            >
                              Hook
                            </label>
                            <Textarea
                              value={reasonDraft.hook}
                              onChange={(e) =>
                                updateReasonDraft(
                                  suggestion.id,
                                  'hook',
                                  e.target.value
                                )
                              }
                              style={{ minHeight: 48 }}
                            />
                            <label
                              style={{
                                fontSize: 'var(--fs-xs)',
                                color: 'var(--text-muted)',
                              }}
                            >
                              Reason
                            </label>
                            <Textarea
                              value={reasonDraft.reason}
                              onChange={(e) =>
                                updateReasonDraft(
                                  suggestion.id,
                                  'reason',
                                  e.target.value
                                )
                              }
                              style={{ minHeight: 120, whiteSpace: 'pre-wrap' }}
                            />
                            <div
                              className="row"
                              style={{
                                justifyContent: 'flex-end',
                                gap: 8,
                                marginTop: 4,
                              }}
                            >
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={savingReasonId === suggestion.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDiscardReason(suggestion.id);
                                }}
                              >
                                Discard
                              </Button>
                              <Button
                                size="sm"
                                variant="primary"
                                disabled={
                                  savingReasonId === suggestion.id ||
                                  (!reasonDraft.hook.trim() &&
                                    !reasonDraft.reason.trim())
                                }
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSaveReason(suggestion.id);
                                }}
                              >
                                {savingReasonId === suggestion.id
                                  ? 'Saving…'
                                  : 'Save'}
                              </Button>
                            </div>
                          </div>
                        </Card>
                      </div>
                    )}

                    {/* Metadata + Actions */}
                    <div
                      className="row"
                      style={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 16,
                        marginTop: 8,
                      }}
                    >
                      <div className="row" style={{ gap: 16 }}>
                        <span
                          style={{
                            fontSize: 'var(--fs-xs)',
                            color: 'var(--text-faint)',
                          }}
                        >
                          Created:{' '}
                          {suggestion.createdAt
                            ? new Date(suggestion.createdAt).toLocaleString()
                            : 'N/A'}
                        </span>
                        {suggestion.isViewed && (
                          <span
                            style={{
                              fontSize: 'var(--fs-xs)',
                              color: 'var(--text-faint)',
                            }}
                          >
                            Viewed: Yes
                          </span>
                        )}
                      </div>
                      <div className="row" style={{ gap: 8 }}>
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={regeneratingReasonId === suggestion.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRegenerateReason(suggestion.id);
                          }}
                        >
                          {regeneratingReasonId === suggestion.id
                            ? 'Regenerating…'
                            : 'Regenerate Reason & Hook'}
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={sendingCardId === suggestion.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSendSuggestionCard(suggestion.id);
                          }}
                        >
                          {sendingCardId === suggestion.id
                            ? 'Sending…'
                            : 'Send Suggestion Card'}
                        </Button>
                        {suggestion.userConversation?.id ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewChat(suggestion.userConversation?.id);
                            }}
                          >
                            View Chat
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={
                              isGenerating &&
                              generatingSuggestionId !== suggestion.id
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              handleGenerateConversation(
                                suggestion.id,
                                matchedUser?.id
                              );
                            }}
                          >
                            {isGenerating &&
                            generatingSuggestionId === suggestion.id
                              ? 'Generating…'
                              : 'Generate Conversation'}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {createSuggestionModal}
    </div>
  );
};

export default UserSuggestions;
